import requestUtils, { authOptions } from 'api/requestUtils'
import { definitions, paths } from '@luxuryescapes/contract-business'
import qs from 'qs'
import { unique } from 'lib/array/arrayUtils'

type BusinessResponse = paths['/api/business-traveller/me']['get']['responses'][200]['content']['application/json']

export enum BUSINESS_ACCOUNT_FETCH_ERRORS {
  NO_BUSINESS_ASSOCIATED = 'This account is not associated with any business!',
  OTHER = 'Something went wrong!',
  INVITE_PENDING = 'This invitation is pending.',
  EMPLOYEE_DEACTIVATED = 'This account has been deactivated.',
  EMPLOYEE_NOT_ACTIVATED = 'This account has not been activated yet.',
}

export interface AccountBusinessResponse {
  business: App.BusinessTraveller.Business
  businessOffices: Array<App.BusinessTraveller.BusinessOffice>
  employee: App.BusinessTraveller.Employee
  employees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  employeeOnboardingStatus: App.BusinessTraveller.EmployeeOnboardingStatus
  displayFlightBudgetNotification: App.BusinessTraveller.DisplayFlightBudgetNotification
}

function mapBusinessTravellerBusinessAccount(result: definitions['employeeSchema']): AccountBusinessResponse {
  const business: App.BusinessTraveller.Business = {
    country: result.business.country,
    id: result.business.id,
    industry: result.business.industry as App.BusinessTraveller.BusinessIndustries,
    name: result.business.name,
    employeeCount: result.business.employeeCount as App.BusinessTraveller.BusinessEmployeeCountOptions,
    businessIdentifier: result.business.businessIdentifier,
    businessIdentifierType: result.business.businessIdentifierType,
    isCreditEnabled: result.business.offersCredits,
    frequentFlyerItems: result.business.frequentFlyerItems,
    enableMerchantFees: result.business.enableMerchantFees,
  }

  const businessOffices: Array<App.BusinessTraveller.BusinessOffice> = result.businessOffices ? result.businessOffices?.map(office => {
    return {
      id: office.id,
      name: office.name,
      address: {
        mainText: office.address.mainText,
        secondaryText: office.address.secondaryText,
      },
      latitude: office.latitude,
      longitude: office.longitude,
    }
  }) : []

  const employee: App.BusinessTraveller.Employee = {
    customerId: result.customerId,
    creditCustomerEmail: result.creditCustomerEmail,
    id: result.id,
    email: result.email,
    position: result.position,
    firstName: result.firstName,
    lastName: result.lastName,
    roles: unique(result.roles),
    onboardingFlags: result.onboardingFlags ?? [],
    employeeFrequentFlyer: result.employeeFrequentFlyer,
    employeeHotelLoyaltyPrograms: result.employeeHotelLoyaltyPrograms,
  }
  const employeeOnboardingStatus = result.onboardingStatus
  const displayFlightBudgetNotification = result.displayFlightBudgetNotification
  const employees = result.employees ? result.employees.map(employee => {
    return {
      customerId: employee.customerId,
      email: employee.email,
      id: employee.id,
      firstName: employee.firstName,
      lastName: employee.lastName,
      employeeFrequentFlyer: employee.employeeFrequentFlyer,
      employeeHotelLoyaltyPrograms: employee.employeeHotelLoyaltyPrograms,
    }
  }) : []

  return { business, businessOffices, employee, employees, employeeOnboardingStatus, displayFlightBudgetNotification }
}

export function buildBusinessTravellerMeQueryString(options: {
  includeEmployees?: boolean
  includeAvailableEmployees?: boolean
}): string {
  const { includeEmployees, includeAvailableEmployees } = options
  const params = {
    ...(includeEmployees && { includeEmployees: 'true' }),
    ...(includeAvailableEmployees && { includeAvailableEmployees: 'true' }),
  }
  const query = qs.stringify(params)
  return query ? `?${query}` : ''
}

function mappedEmployeeErrorMessage(error: string) {
  switch (error) {
    case 'Invitation pending':
      return BUSINESS_ACCOUNT_FETCH_ERRORS.INVITE_PENDING
    case 'Employee deactivated':
      return BUSINESS_ACCOUNT_FETCH_ERRORS.EMPLOYEE_DEACTIVATED
    case 'Employee not activated':
      return BUSINESS_ACCOUNT_FETCH_ERRORS.EMPLOYEE_NOT_ACTIVATED
    default:
      return BUSINESS_ACCOUNT_FETCH_ERRORS.OTHER
  }
}

async function requestGetAccountBusiness(options: {
  accessToken?: string
  includeEmployees?: boolean
  includeAvailableEmployees?: boolean
}) {
  try {
    const { accessToken, includeEmployees, includeAvailableEmployees } = options
    const queryString = buildBusinessTravellerMeQueryString({ includeEmployees, includeAvailableEmployees })
    const response = await requestUtils.get<BusinessResponse>(
      `/api/business-traveller/me${queryString}`,
      authOptions(accessToken),
    )

    if (!response.result.length) {
      return Promise.reject(BUSINESS_ACCOUNT_FETCH_ERRORS.NO_BUSINESS_ASSOCIATED)
    }

    const serverData = response.result[0]
    if ('error' in serverData) {
      return Promise.reject(mappedEmployeeErrorMessage(serverData.error))
    }
    return mapBusinessTravellerBusinessAccount(serverData)
  } catch {
    return Promise.reject(BUSINESS_ACCOUNT_FETCH_ERRORS.OTHER)
  }
}

export default requestGetAccountBusiness
